import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Datatable} from '../../../../components/Datatable/Datatable'
import type {MetaType} from '../../../../components/Datatable/Datatable'
import {Button} from '../../../../components/Button/Button'
import {Loading} from '../../../../components/Loading/Loading'
import {POST, PUT, DELETE} from '../../../../services/api'
import {ModalWrapper} from '../../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useModals from '../../../../hooks/useModals'
import {ModalDelete} from '../../../../components/Modals/General/ModalDelete/ModalDelete'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import {useSwitchStatus} from '../../../../components/Datatable/SwitchStatus'

type HotelServicePost = {
  id: number
  title: string
  content: string | null
  img: string | null
  status: boolean
  contentHotelService?: Array<{
    day: string
    able: boolean
    periods: Array<{id: string; startTime: string; endTime: string; interval: number}>
  }>
}

const BookingIndexContent: FC = () => {
  const navigate = useNavigate()
  const {showDeleteModal} = useModals()
  const [loading, setLoading] = useState(false)
  const {switchStatus} = useSwitchStatus()
  const [posts, setPosts] = useState<HotelServicePost[]>([])
  const [id, setId] = useState<number | null>(null)
  const [meta, setMeta] = useState<MetaType | undefined>(undefined)

  const getPosts = async () => {
    try {
      setLoading(true)
      const res = await POST(`/post/filter`, {type: 'hotel_service'})

      if (!res || !res.data) {
        setPosts([])
        setMeta(res?.meta)
        setLoading(false)
        return
      }

      const mapped = res.data.map((row: any) => ({
        id: row.id,
        title: row.title,
        content: row.content,
        img: row.img,
        status: row.status,
        order: row.order,
        created: row.created,
        contentHotelService: row.contentHotelService,
      }))

      setPosts(mapped)
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      console.log(e)
      setLoading(false)
    }
  }

  useEffect(() => {
    getPosts()
  }, [])

  const deletePost = async () => {
    try {
      if (!id) return
      setLoading(true)
      const res = await DELETE(`/post/${id}`)
      if (res && res.success) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('general.success')),
          text: String(i18n.t('booking_hotel_services.delete_success')),
        })
        await getPosts()
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('general.error')),
          text: String(i18n.t('booking_hotel_services.delete_error')),
        })
        await getPosts()
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('general.error')),
        text: String(i18n.t('booking_hotel_services.delete_error')),
      })
      await getPosts()
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {String(i18n.t('booking_hotel_services.page_title'))}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {String(i18n.t('booking_hotel_services.page_subtitle'))}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={String(i18n.t('booking_hotel_services.create_button'))}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{type: 'button'}}
                  click={() => navigate('/hospitality/context/booking_config_hotel/create')}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={posts}
                headers={[
                  {
                    key: String(i18n.t('booking_hotel_services.table_image')),
                    value: 'img',
                    type: 'image',
                  },
                  {
                    key: String(i18n.t('booking_hotel_services.table_title')),
                    value: 'title',
                    type: 'default',
                  },
                  //   {key: 'Ordem', value: 'order', type: 'default'},
                  {
                    key: String(i18n.t('booking_hotel_services.table_status')),
                    value: 'status',
                    type: 'check',
                    action: (item: any) =>
                      switchStatus({endpoint: 'post', Id: item.id, status: item.status}),
                  },
                  {
                    key: String(i18n.t('booking_hotel_services.table_actions')),
                    value: 'acoes',
                    type: '',
                  },
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    title: String(i18n.t('booking_hotel_services.action_edit')),
                    action: (item: any) =>
                      navigate(`/hospitality/context/booking_config_hotel/edit/${item.id}`),
                  },
                  {
                    icon: 'fas fa-trash',
                    title: String(i18n.t('booking_hotel_services.action_delete')),
                    action: (item: any) => {
                      showDeleteModal(String(i18n.t('booking_hotel_services.delete_confirm')))
                      setId(item.id)
                    },
                  },
                ]}
                color='primary'
                meta={meta}
              />
            </div>
          </div>
        </div>
      )}
      <ModalDelete confirmAction={() => deletePost()} />
    </>
  )
}

const BookingIndex: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: String(i18n.t('booking_hotel_services.breadcrumb_dashboard')),
            path: 'dashboard',
            isActive: true,
          },
          {
            title: String(i18n.t('booking_hotel_services.breadcrumb_services')),
            path: 'booking-hotel',
            isActive: true,
          },
        ]}
      >
        {String(i18n.t('booking_hotel_services.breadcrumb_booking'))}
      </PageTitle>
      <BookingIndexContent />
    </>
  )
}

export {BookingIndex}
